import { useNavigate } from 'react-router-dom';

/**
 * Privacy Notice — GDPR Article 13/14 compliance
 * Accessible from the login page footer and app navigation.
 */
export default function PrivacyNotice() {
  const navigate = useNavigate();

  return (
    <div style={{
      maxWidth: '720px',
      margin: '2rem auto',
      padding: '2rem',
      fontFamily: 'var(--font-family-body, sans-serif)',
      color: 'var(--text-primary)',
      lineHeight: 1.6,
    }}>
      <button
        type="button"
        className="unstyled"
        onClick={() => navigate(-1)}
        style={{
          all: 'unset',
          cursor: 'pointer',
          fontSize: '0.875rem',
          color: 'var(--text-tertiary)',
          marginBottom: '1rem',
          display: 'inline-block',
        }}
        aria-label="Go back"
      >
        &larr; Back
      </button>

      <h1 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Privacy Notice</h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
        Last updated: April 2026
      </p>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>1. Data Controller</h2>
        <p>
          The National College of Art and Design (NCAD), 100 Thomas Street, Dublin 8, Ireland is the
          data controller for personal data processed through the ComDesBook Equipment Booking System
          (www.cdbooking.com).
        </p>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>2. Purpose of Processing</h2>
        <p>We process your personal data to:</p>
        <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
          <li>Manage equipment bookings and returns for the Communication Design department</li>
          <li>Verify your identity and manage your account</li>
          <li>Track equipment availability and usage</li>
          <li>Enforce booking policies (including late returns, fines, and strike records)</li>
          <li>Generate anonymised usage analytics for departmental planning</li>
          <li>Send booking confirmations and reminders via email</li>
        </ul>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>3. Legal Basis</h2>
        <p>
          We process your data under <strong>Article 6(1)(e) GDPR</strong> — processing necessary for
          the performance of a task carried out in the public interest (institutional equipment management
          for educational purposes). For disciplinary records (strikes, fines), the legal basis is
          <strong> Article 6(1)(f)</strong> — legitimate interest in maintaining equipment availability
          and accountability.
        </p>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>4. Personal Data We Collect</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', marginTop: '0.5rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Data</th>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.5rem' }}>Name, email, department</td>
              <td style={{ padding: '0.5rem' }}>Account identity and communication</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.5rem' }}>Booking history</td>
              <td style={{ padding: '0.5rem' }}>Equipment reservation management</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.5rem' }}>Strike and fine records</td>
              <td style={{ padding: '0.5rem' }}>Policy enforcement and accountability</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.5rem' }}>Login activity</td>
              <td style={{ padding: '0.5rem' }}>Security monitoring</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>5. Data Storage and Security</h2>
        <p>
          Your data is stored in a Cloudflare D1 database hosted within the European Economic Area (EEA).
          All data in transit is encrypted via TLS. Passwords are hashed using PBKDF2 with 100,000
          iterations and are never stored in plaintext. Access to personal data is restricted by
          role-based permissions.
        </p>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>6. Third-Party Processors</h2>
        <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
          <li>
            <strong>Cloudflare, Inc.</strong> — hosting, database, and AI features. Cloudflare processes
            data under their Data Processing Agreement and does not use customer data for model training.
          </li>
          <li>
            <strong>Resend</strong> — email delivery for booking confirmations, password setup links,
            and admin notifications. Only your email address and booking details are shared.
            Resend processes data under their Data Processing Agreement.
          </li>
        </ul>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>7. Data Retention</h2>
        <p>
          Your account and booking data are retained for the duration of your enrollment or employment
          at NCAD. After you leave the institution, your data will be anonymised or deleted within
          6 months of account deactivation. Audit logs are retained for 3 years for compliance purposes.
        </p>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>8. Your Rights</h2>
        <p>Under GDPR, you have the right to:</p>
        <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
          <li><strong>Access</strong> — request a copy of all personal data we hold about you</li>
          <li><strong>Rectification</strong> — correct inaccurate personal data</li>
          <li><strong>Erasure</strong> — request deletion of your personal data</li>
          <li><strong>Portability</strong> — receive your data in a machine-readable format</li>
          <li><strong>Object</strong> — object to processing based on legitimate interest</li>
          <li><strong>Restriction</strong> — request that we limit how we use your data</li>
        </ul>
        <p style={{ marginTop: '0.75rem' }}>
          You can exercise two of these rights yourself, from inside the app: sign in and open{' '}
          <strong>Me &rarr; Profile &rarr; Your data</strong>. <strong>Download my data</strong>{' '}
          gives you a machine-readable copy of everything we hold about you (access and
          portability), and <strong>Request account deletion</strong> sends an erasure request
          to the booking administrators, who action it by hand — it cannot complete while you
          have equipment on loan or unpaid fines, and the records described in section 7 are
          retained. For any other right, contact your department administrator or email the
          data protection contact below.
        </p>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>9. Browser Storage</h2>
        <p>
          This application uses browser localStorage to store your authentication token and UI
          preferences. This storage is strictly necessary for the service to function and does not
          require consent. No third-party tracking cookies are used.
        </p>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>10. Contact</h2>
        <p>
          For data protection enquiries or to exercise your rights, please contact
          your department administrator.
        </p>
        <p style={{ marginTop: '0.75rem' }}>
          You also have the right to lodge a complaint with the Irish Data Protection
          Commission (DPC) at <strong>www.dataprotection.ie</strong>.
        </p>
      </section>
    </div>
  );
}
